import { useState, useCallback } from 'react';
import { RegionDTO } from '../types/parameters.types';

export type ModalStep = 'selectBase' | 'editRegion';

export interface UseCreateRegionModalReturn {
	step: ModalStep;
	isEditMode: boolean;
	selectedStateCodes: string[];
	regionName: string;
	searchQuery: string;
	handleSelectBase: (region: RegionDTO) => void;
	handleSkipBase: () => void;
	handleToggleState: (stateCode: string) => void;
	setSearchQuery: (q: string) => void;
	setRegionName: (name: string) => void;
	handleConfirm: () => void;
}

export const useCreateRegionModal = (
	onConfirm: (name: string, stateCodes: string[]) => void,
	initialRegion?: RegionDTO
): UseCreateRegionModalReturn => {
	const isEditMode = !!initialRegion;
	const [step, setStep] = useState<ModalStep>(isEditMode ? 'editRegion' : 'selectBase');
	const [selectedStateCodes, setSelectedStateCodes] = useState<string[]>(
		initialRegion?.stateCodes ?? []
	);
	const [regionName, setRegionName] = useState(initialRegion?.name ?? 'Minha Nova Região');
	const [searchQuery, setSearchQuery] = useState('');

	const handleSelectBase = useCallback((region: RegionDTO) => {
		setSelectedStateCodes(region.stateCodes);
		setStep('editRegion');
	}, []);

	const handleSkipBase = useCallback(() => {
		setStep('editRegion');
	}, []);

	const handleToggleState = useCallback((stateCode: string) => {
		setSelectedStateCodes((prev) =>
			prev.includes(stateCode) ? prev.filter((c) => c !== stateCode) : [...prev, stateCode]
		);
	}, []);

	const handleConfirm = useCallback(() => {
		onConfirm(regionName, selectedStateCodes);
	}, [onConfirm, regionName, selectedStateCodes]);

	return {
		step,
		isEditMode,
		selectedStateCodes,
		regionName,
		searchQuery,
		handleSelectBase,
		handleSkipBase,
		handleToggleState,
		setSearchQuery,
		setRegionName,
		handleConfirm,
	};
};
